import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import { type FC, useCallback, useEffect } from 'react';
import { checkRubikaToken, checkShadToken, isGuid } from '@/Module/Auth/Helper';
import { useLogin } from '@/Module/Auth/Hook';
import type { IAuth } from '@/Module/Auth/Model/DataModel';
import { authSetIsTransitioningAction } from '@/Module/Auth/Store/Slice/AuthSlice';
import { LINKS } from '@/Module/Core/Constant';
import { hasHappened } from '@/Module/Core/Helper';
import { CookieService } from '@/Module/Core/Service';
import { useAppDispatch, useAppSelector } from '@/Module/Store/Hook';
import { getSubscriptionsV2UserAction } from '@/Module/Subscription/Store/Action';
interface UrlObject {
  pathname: string;
  query: {
    result: string;
  };
}
const RUBIKA_KEY = 'messenger_rubika_auth';
const SHAD_KEY = 'messenger_shad_auth';
const AuthMessengerContainer: FC = () => {
  const router = useRouter();
  const {
    loginViaMessenger
  } = useLogin();
  const dispatch = useAppDispatch();
  const rubikaToken = useAppSelector(s => s.auth.rubikaToken);
  const shadToken = useAppSelector(s => s.auth.shadToken);
  const safePush = useCallback((url: string | UrlObject) => {
    const target = typeof url === 'string' ? url : url.pathname + (url.query ? `?${new URLSearchParams(url.query).toString()}` : '');
    if (router.asPath !== target) router.push(url).catch(err => {
      const message = (err as Error).message || 'Unknown error';
      Sentry.captureEvent({
        message,
        level: 'error',
        logger: 'AuthMessenger - Rubika'
      });
    });
    // dispatch(authSetIsTransitioningAction(false));
  }, [router, dispatch]);

  // RUBIKA
  useEffect(() => {
    if (!router.isReady || !rubikaToken) return;
    if (typeof window === 'undefined') return;
    const already = hasHappened(RUBIKA_KEY, 5, 5);
    if (already) return;
    if (rubikaToken.length !== 50) return;
    (async () => {
      try {
        dispatch(authSetIsTransitioningAction(true));
        localStorage.setItem(RUBIKA_KEY, Date.now().toString());
        CookieService.setRubikaToken(rubikaToken);
        const result = await checkRubikaToken(dispatch, {
          token: rubikaToken
        });
        if (result?.type === 'INVALID_TOKEN') {
          safePush(LINKS.AUTH.RUBIKA_LOGOUT);
          return;
        }
        if (result?.type !== 'JWT') {
          safePush(LINKS.LEARNING);
          return;
        }
        await loginViaMessenger(result.value as IAuth);
        const list = await dispatch(getSubscriptionsV2UserAction(result.value.accessToken)).unwrap();
        const plan = list.data.find(p => p.subscriptionPlanId === 199);
        const isActive = plan && new Date(plan.endDate) >= new Date();
        safePush(isActive ? LINKS.LEARNING : LINKS.SUBSCRIPTION.RUBIKA);
      } catch (e) {
        const message = (e as Error).message || 'Unknown error';
        Sentry.captureEvent({
          message,
          level: 'error',
          logger: 'AuthMessenger - Rubika'
        });
        safePush(LINKS.LEARNING);
      }
    })();
  }, [router.isReady, rubikaToken, dispatch]);

  // SHAD
  useEffect(() => {
    if (!router.isReady || !shadToken) return;
    if (typeof window === 'undefined') return;
    if (hasHappened(SHAD_KEY, 5, 5) || !isGuid(shadToken)) return;
    (async () => {
      try {
        dispatch(authSetIsTransitioningAction(true));
        localStorage.setItem(SHAD_KEY, Date.now().toString());
        CookieService.setShadToken(shadToken);
        const result = await checkShadToken(dispatch, {
          token: shadToken
        });
        if (result?.type === 'JWT') {
          await loginViaMessenger(result.value as IAuth);
          safePush(LINKS.LEARNING);
          return;
        }
        if (result?.message?.includes('503')) {
          safePush({
            pathname: LINKS.AUTH.SHAD_LOGOUT,
            query: {
              result: '503'
            }
          });
        }
      } catch (e) {
        const message = (e as Error).message || 'Unknown error';
        Sentry.captureEvent({
          message,
          level: 'error',
          logger: 'AuthMessenger - Shad'
        });
        safePush(LINKS.LEARNING);
      }
    })();
  }, [router.isReady, shadToken, dispatch]);
  return null;
};
export default AuthMessengerContainer;